import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type React from 'react';
import { createElement, useEffect, useMemo, useState } from 'react';
import { MdFullscreen, MdTableChart } from 'react-icons/md';
import { DownloadIcon } from '@/assets/icons';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftDatePicker, { type DateRange } from '@/components/Molecules/FaceliftDatePicker';
import type { TableData } from '@/components/Organisms/ChartWithTableView';
import ChartWithTableView from '@/components/Organisms/ChartWithTableView';
import LineChart from '@/components/Organisms/LineChart';
import type { LineChartCategory, LineChartFilter } from '@/components/Organisms/LineChart/LineChart.types';
import { colors } from '@/components/ui/theme';
import { useGetPayorsCollectionsTrendMonthly } from '@/hooks/service-hooks/collection';
import { calculateChartDateRange, chartDateFilterOptions, DEFAULT_CHART_DATE_FILTER } from '@/utils/dateFilters';
import { generateCSVFromTable, generateExcelFromTable } from '@/utils/exportFiles';
import type { ProfileMenuItem } from '@/utils/profileMenuItems';
import type { MonthlyPayorCollectionLineChartProps } from './MonthlyPayorCollectionLineChart.types';

dayjs.extend(utc);
dayjs.extend(timezone);

const normalizeSeriesTitle = (value: unknown) => {
	return typeof value === 'string' && value.trim().length > 0 ? value : 'Series';
};

const MonthlyPayorCollectionLineChart: React.FC<MonthlyPayorCollectionLineChartProps> = ({
	title = '',
	isLoading: propIsLoading = false,
	height = 450,
	showFilters = false,
	showTableMenu = false,
	clinicsProductsFilter = '61aea445-e3b4-ea11-a816-000d3a191041|;',
	...rest
}) => {
	// State for menu and table modal
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isTableModalOpen, setIsTableModalOpen] = useState(false);
	// Color scheme for different payors
	const chartColors = [
		colors.chartGradient1,
		colors.chartGradient4, 
		colors.chartGradient2, 
		colors.chartGradient3, 
		colors.chartGradient5,
		colors.chartGradient6,
	];

	// State for selected date filter (default to Year to Date)
	const [selectedFilter, setSelectedFilter] = useState<SelectItem | null>(DEFAULT_CHART_DATE_FILTER);
	const [isDateFilterModalOpen, setIsDateFilterModalOpen] = useState<boolean>(false);
	const [customDateRange, setCustomDateRange] = useState<DateRange>({
		startDate: '',
		endDate: '',
	});

	// Calculate date range based on selected filter
	const [dateRange, setDateRange] = useState<{ start: string; end: string }>(
		selectedFilter ? calculateChartDateRange(selectedFilter.value as string) || { start: '', end: '' } : { start: '', end: '' }
	);

	// Fetch data from API when showFilters is true
	const { data: apiResponse, isFetching } = useGetPayorsCollectionsTrendMonthly(
		{
			clinicsProductsFilter,
			multi_filter: '',
			date_filter_range: `${dateRange.start},${dateRange.end}`,
		},
		showFilters,
	);

	const isLoading = showFilters ? isFetching : propIsLoading;

	// Transform API data to LineChart format
	const chartData: LineChartCategory[] = useMemo(() => {
		if (!apiResponse?.data || apiResponse.data.length === 0) {
			return [];
		}

		const rawData = apiResponse.data;

		// Get unique payors
		const payorSet = new Set<string>();
		rawData.forEach((item) => {
			payorSet.add(normalizeSeriesTitle((item as any)?.primaryInsurance));
		});
		const payors = Array.from(payorSet);

		// Group data by date (keep individual dates, not aggregated)
		const dateMap = new Map<string, Map<string, number>>();
		
		rawData.forEach((item) => {
			const date = new Date(item.PaidDate);
			// Format as M/D/YYYY
			const dateKey = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
			
			if (!dateMap.has(dateKey)) {
				dateMap.set(dateKey, new Map<string, number>());
			}

			const payorMap = dateMap.get(dateKey)!;
			const payor = normalizeSeriesTitle((item as any)?.primaryInsurance);
			const currentCost = payorMap.get(payor) || 0;
			payorMap.set(payor, currentCost + item.cost);
		});

		// Sort dates chronologically
		const sortedDates = Array.from(dateMap.keys()).sort((a, b) => {
			const dateA = new Date(a);
			const dateB = new Date(b);
			return dateA.getTime() - dateB.getTime();
		});

		// Convert to LineChart format
		return sortedDates.map((dateKey) => {
			const payorMap = dateMap.get(dateKey) || new Map<string, number>();

		return {
				category: dateKey,
				data: payors.map((payor) => ({
					title: payor,
					value: payorMap.get(payor) || 0,
				})),
		};
		});
	}, [apiResponse]);

	// Prepare table data for ChartWithTableView
	const tableData: TableData = useMemo(() => {
		if (!chartData || chartData.length === 0) {
			return { columns: [], rows: [] };
		}

		// Get all unique payors from the data
		const payors = new Set<string>();
		chartData.forEach(item => {
			item.data.forEach(d => payors.add(d.title));
		});
		const payorList = Array.from(payors);

		// Create columns: Date + one column per payor
		const columns = [
			{ key: 'date', label: 'Date', align: 'left' as const },
			...payorList.map(payor => ({
				key: payor,
				label: payor,
				align: 'right' as const,
				render: (value: number | undefined) => value !== undefined ? `$${value.toLocaleString()}` : '-'
			}))
		];

		// Create rows: one row per date
		const rows = chartData.map(item => {
			const row: Record<string, any> = { date: item.category };
			item.data.forEach(d => {
				row[d.title] = d.value;
			});
			return row;
		});

		return { columns, rows };
	}, [chartData]);

	// Export functions
	const exportAsCSV = () => {
		if (!tableData.rows || tableData.rows.length === 0) return;
		
		const columns = tableData.columns.map(col => ({
			key: col.key,
			label: col.label
		}));
		generateCSVFromTable(columns, tableData.rows, 'MonthlyPayorCollectionTrend', true);
	};

	const exportAsExcel = () => {
		if (!tableData.rows || tableData.rows.length === 0) return;
		
		const columns = tableData.columns.map(col => ({
			key: col.key,
			label: col.label
		}));
		generateExcelFromTable(columns, tableData.rows, 'MonthlyPayorCollectionTrend', true);
	};

	// Handle menu item click
	const handleMenuNavigate = (href?: string) => {
		if (href === '#view-table') {
			setIsTableModalOpen(true);
		} else if (href === '#download-csv') {
			exportAsCSV();
		} else if (href === '#download-xlsx') {
			exportAsExcel();
		}
		// Note: fullscreen is handled inside LineChart component
	};

	// Chart options menu items
	const chartMenuItems: ProfileMenuItem[] = useMemo(() => [
		{
			title: 'View Table',
			icon: createElement(MdTableChart, { width: 18, height: 18 }),
			href: '#view-table',
		},
		{
			title: 'View in full screen',
			icon: createElement(MdFullscreen, { width: 18, height: 18 }),
			href: '#view-fullscreen',
		},
		{
			title: 'Download as CSV',
			icon: createElement(DownloadIcon, { width: 18, height: 18 }),
			href: '#download-csv',
		},
		{
			title: 'Download as XLSX',
			icon: createElement(DownloadIcon, { width: 18, height: 18 }),
			href: '#download-xlsx',
		},
	], []);

	// Handle filter change
	const handleFilterChange = (newValue: SelectItem) => {
		setSelectedFilter(newValue);
		// If custom is selected, open the modal (useEffect will also handle this)
		if (newValue.value === 'custom') {
			setIsDateFilterModalOpen(true);
		}
	};

	// Watch for selectedFilter changes to handle date range updates
	useEffect(() => {
		if (selectedFilter?.value === 'custom') {
			setIsDateFilterModalOpen(true);
		} else if (selectedFilter?.value) {
			setDateRange(calculateChartDateRange(selectedFilter.value as string) || { start: '', end: '' });
		}
	}, [selectedFilter]);

	// Setup filters for LineChart
	const filters: LineChartFilter[] = useMemo(() => {
		if (!showFilters) return [];

		return [
			{
				label: 'Select Date Range',
				options: chartDateFilterOptions,
				defaultValue: selectedFilter || undefined,
				onChange: handleFilterChange,
			},
		];
	}, [showFilters, selectedFilter]);

	// Dynamic thresholds at 80%, 50%, and 20% of max value
	const thresholds = useMemo(() => [
		{ value: '80%', label: 'Max' },
		{ value: '50%', label: 'Med' },
		{ value: '20%', label: 'Min' },
	], []);

	return (
		<ChartWithTableView 
			title={title} 
			tableData={tableData} 
			showMenu={false}
			isTableModalOpen={isTableModalOpen}
			setIsTableModalOpen={setIsTableModalOpen}
		>
			<LineChart
				title={title}
				data={chartData}
				isLoading={isLoading}
				height={height}
				filters={filters}
				colors={chartColors}
				showCurrencyInTooltip={true}
				dynamicYAxis={true}
				maxSeriesNameLength={35}
				thresholds={thresholds}
				showTableMenu={showTableMenu}
				isMenuOpen={isMenuOpen}
				onMenuOpenChange={setIsMenuOpen}
				onViewTable={handleMenuNavigate}
				menuItems={chartMenuItems}
					{...rest}
			/>
			<FaceliftDialog
				title="Select Period"
				open={isDateFilterModalOpen}
				onOpenChange={(open) => {}}
				sx={{
					maxWidth: 400,
					maxHeight: 400,
				}}
				footer={
					<>
						<FaceliftButton
							width="100px"
							bg="transparent"
							color={{ _light: 'gray.600', _dark: 'gray.100' }}
							border="1px solid"
							borderColor={{ _light: 'gray.600', _dark: 'gray.100' }}
							onClick={() => {
								setIsDateFilterModalOpen(false);
							}}
						>
							Close
						</FaceliftButton>
						<FaceliftButton
							width="100px"
							onClick={() => {
								if (customDateRange.startDate && customDateRange.endDate) {
									// Convert DateRange format (startDate/endDate) to chart format (start/end)
									// DateRange uses MM/DD/YYYY format, convert to YYYY-MM-DD
									const parsedStartDate = dayjs(customDateRange.startDate, 'MM/DD/YYYY');
									const parsedEndDate = dayjs(customDateRange.endDate, 'MM/DD/YYYY');
									
									// Validate dates are valid before using
									if (parsedStartDate.isValid() && parsedEndDate.isValid()) {
										setDateRange({ 
											start: parsedStartDate.format('YYYY-MM-DD'), 
											end: parsedEndDate.format('YYYY-MM-DD') 
										});
										setIsDateFilterModalOpen(false);
									}
								}
							}}
							disabled={!customDateRange.startDate || !customDateRange.endDate}
						>
							Apply
						</FaceliftButton>
					</>
				}
			>
				<FaceliftDatePicker
					isRange={true}
					range={customDateRange}
					onRangeChange={setCustomDateRange}
					placeholder="Choose period..."
				/>
			</FaceliftDialog>
		</ChartWithTableView>
	);
};

export default MonthlyPayorCollectionLineChart;
